/**
 * Order-preserving JSON parser.
 *
 * Standard JSON.parse() (and therefore Object.keys()) reorders "integer-like"
 * string keys numerically before other keys. That breaks the visual/source
 * order of purity keys such as "916HUID","750HUID","840","650","580" in
 * product.json, which should be displayed in the exact order they appear in
 * the file.
 *
 * This parser walks the raw JSON text and rebuilds objects using a Map-like
 * internal structure that preserves insertion order for every key, including
 * integer-like ones. It returns plain objects whose [[Keys]] enumeration
 * follows the original file order.
 */

function parseValue(scanner) {
  scanner.skipWhitespace();
  const ch = scanner.peek();

  if (ch === "{") return parseObject(scanner);
  if (ch === "[") return parseArray(scanner);
  if (ch === '"') return parseString(scanner);
  if (ch === "t") return parseLiteral(scanner, "true", true);
  if (ch === "f") return parseLiteral(scanner, "false", false);
  if (ch === "n") return parseLiteral(scanner, "null", null);
  return parseNumber(scanner);
}

function parseObject(scanner) {
  scanner.expect("{");
  const keys = [];
  const values = [];

  scanner.skipWhitespace();
  if (scanner.peek() === "}") {
    scanner.expect("}");
    return createOrderedObject(keys, values);
  }

  while (true) {
    scanner.skipWhitespace();
    const key = parseString(scanner);
    scanner.skipWhitespace();
    scanner.expect(":");
    const value = parseValue(scanner);

    keys.push(key);
    values.push(value);

    scanner.skipWhitespace();
    const sep = scanner.peek();
    if (sep === ",") {
      scanner.advance();
      continue;
    }
    if (sep === "}") {
      scanner.expect("}");
      break;
    }
    throw new Error("Expected ',' or '}' in object at position " + scanner.pos);
  }

  return createOrderedObject(keys, values);
}

function parseArray(scanner) {
  scanner.expect("[");
  const items = [];

  scanner.skipWhitespace();
  if (scanner.peek() === "]") {
    scanner.expect("]");
    return items;
  }

  while (true) {
    const value = parseValue(scanner);
    items.push(value);

    scanner.skipWhitespace();
    const sep = scanner.peek();
    if (sep === ",") {
      scanner.advance();
      continue;
    }
    if (sep === "]") {
      scanner.expect("]");
      break;
    }
    throw new Error("Expected ',' or ']' in array at position " + scanner.pos);
  }

  return items;
}

function parseString(scanner) {
  scanner.expect('"');
  let result = "";
  while (scanner.pos < scanner.text.length) {
    const ch = scanner.advance();
    if (ch === '"') return result;
    if (ch === "\\") {
      const esc = scanner.advance();
      switch (esc) {
        case '"': result += '"'; break;
        case "\\": result += "\\"; break;
        case "/": result += "/"; break;
        case "b": result += "\b"; break;
        case "f": result += "\f"; break;
        case "n": result += "\n"; break;
        case "r": result += "\r"; break;
        case "t": result += "\t"; break;
        case "u": {
          const hex = scanner.text.slice(scanner.pos, scanner.pos + 4);
          scanner.pos += 4;
          result += String.fromCharCode(parseInt(hex, 16));
          break;
        }
        default:
          result += esc;
      }
      continue;
    }
    result += ch;
  }
  throw new Error("Unterminated string at position " + scanner.pos);
}

function parseNumber(scanner) {
  const start = scanner.pos;
  if (scanner.peek() === "-") scanner.advance();
  while (scanner.pos < scanner.text.length) {
    const ch = scanner.peek();
    if (/[0-9.eE+]/.test(ch)) scanner.advance();
    else if (ch === "-" && scanner.pos === start) scanner.advance();
    else break;
  }
  const numText = scanner.text.slice(start, scanner.pos);
  const num = Number(numText);
  if (Number.isNaN(num)) throw new Error("Invalid number at position " + start);
  return num;
}

function parseLiteral(scanner, expected, value) {
  if (scanner.text.slice(scanner.pos, scanner.pos + expected.length) !== expected) {
    throw new Error("Unexpected token at position " + scanner.pos);
  }
  scanner.pos += expected.length;
  return value;
}

/**
 * Build a plain object whose key enumeration order matches the supplied
 * insertion order, even when keys are integer-like (which V8 would otherwise
 * sort numerically).
 */
function createOrderedObject(keys, values) {
  const obj = {};
  for (let i = 0; i < keys.length; i++) {
    obj[keys[i]] = values[i];
  }

  // Freeze a hidden list of keys so Object.keys(obj) returns them in order.
  // We attach it as a non-enumerable, hidden property using a marker that
  // JSON.parse would never produce.
  const orderedKeys = keys;
  Object.defineProperty(obj, "__orderedKeys__", {
    value: orderedKeys,
    enumerable: false,
    writable: false,
    configurable: true,
  });

  return obj;
}

class Scanner {
  constructor(text) {
    this.text = text;
    this.pos = 0;
  }

  peek() {
    return this.pos < this.text.length ? this.text[this.pos] : "";
  }

  advance() {
    return this.text[this.pos++];
  }

  expect(ch) {
    if (this.peek() !== ch) {
      throw new Error(
        "Expected '" + ch + "' at position " + this.pos + " but got '" + this.peek() + "'",
      );
    }
    this.advance();
  }

  skipWhitespace() {
    while (this.pos < this.text.length) {
      const ch = this.text[this.pos];
      if (ch === " " || ch === "\n" || ch === "\r" || ch === "\t") this.pos++;
      else break;
    }
  }
}

/**
 * Parse a JSON string while preserving the original key order from the source
 * text (including integer-like keys such as "580", "650", "840").
 */
function parseOrderPreservingJson(text) {
  const scanner = new Scanner(text);
  const result = parseValue(scanner);
  scanner.skipWhitespace();
  if (scanner.pos !== scanner.text.length) {
    throw new Error(
      "Trailing content after JSON at position " + scanner.pos,
    );
  }
  return result;
}

/**
 * Get the keys of an order-preserving object in their original insertion
 * order. Falls back to Object.keys for plain objects.
 */
function orderedKeysOf(obj) {
  if (obj && typeof obj === "object" && !Array.isArray(obj)) {
    const hidden = obj.__orderedKeys__;
    if (Array.isArray(hidden)) return hidden.slice();
  }
  return Object.keys(obj);
}

export {
  parseOrderPreservingJson,
  orderedKeysOf,
};